import { useState, useEffect, useMemo } from 'react';
import { Banknote, Clock, CreditCard, Receipt, CalendarClock, Code2 } from 'lucide-react';
import FleetPaymentsPage from '../fleet-payments/FleetPaymentsPage';
import WorkdayEarningsPage from '../workday-earnings/WorkdayEarningsPage';
import CourierPaymentsPage from '../courier-payments/CourierPaymentsPage';
import InvoicesPage from '../invoices/InvoicesPage';
import SoftwareFeePage from '../software-fee/SoftwareFeePage';
import Notice from '../../components/common/Notice';
import {
    nextClosing, humanizeUntil, formatClosing, formatWeekRange, isoWeekOf,
    weekFlowStep, compareWeeks, FLOW_ORDER, isFlowComplete,
} from './payoutPeriod';
import { getWeekDateRange } from '../../utils/payoutWeek';
import './PayoutsPage.css';

/**
 * Hakedişler — tek ekran.
 *
 * Dört ayrı menü girişi (Filo Hakedişleri / Mesai Hakedişleri / Kurye Hakedişleri / Faturalar)
 * aynı paranın dört görünümüydü; kullanıcı "kaydım nerede" sorusunu ekran değiştirerek arıyordu.
 * Sekmeler aynı yerde durur, üstteki hat parayı hangi adımda olduğunu gösterir.
 */

const TABS = [
    {
        id: 'fleet',
        label: 'Filo hakedişi',
        icon: Banknote,
        summary: 'Haftanın paketleri restoranla yapılan anlaşma modeline göre ayrılır. Hızlıyo\'nun tahsil ettiği paketler hakedişe dönüşür; faturasını siz kesersiniz.',
    },
    {
        id: 'workday',
        label: 'Mesai hakedişi',
        icon: Clock,
        summary: 'Saatlik veya yövmiyeli çalışan kuryelerin gün gün mesai kaydı. Her gece 03:00\'te üretilir.',
    },
    {
        id: 'courier',
        label: 'Kurye hakedişi',
        icon: CreditCard,
        summary: 'Kuryelerinizin haftalık hakediş kayıtları: paket kazancı, mesai ve paket bonusu toplamı.',
    },
    {
        id: 'invoices',
        label: 'Faturalar',
        icon: Receipt,
        summary: 'Hakedişe karşılık yüklenen faturalar ve onay durumları.',
    },
    {
        // Diğer dördünün TERSİ yön: burada para sizden çıkıyor. Ayrı sekme olmasının
        // sebebi bu — aynı listeye koymak muhasebenin ters kayıt açmasına yol açardı.
        id: 'software-fee',
        label: 'Yazılım bedeli',
        icon: Code2,
        summary: 'Entegrasyon kanalından teslim edilen paketler için Hızlıyo\'nun size kestiği aylık fatura. Aylık dönem.',
    },
];

/** Paranın izlediği yol — her adım gerçek bir durum değişimi, süs değil. */
const FLOW = [
    { key: 'week', label: 'Hafta sürüyor', detail: 'Teslimatlar dönem havuzunda toplanır' },
    { key: 'close', label: 'Dönem kapanır', detail: 'Pazartesi 07:00 filo, 08:00 kurye' },
    { key: 'invoice', label: 'Fatura yüklenir', detail: 'Tutar hakedişle birebir aynı olmalı' },
    { key: 'approve', label: 'Hızlıyo onaylar', detail: 'Tutar ve belge kontrolü' },
    { key: 'paid', label: 'Ödeme yapılır', detail: 'Hakediş "Ödendi" durumuna geçer' },
];

const VALID_TABS = TABS.map(t => t.id);

const PayoutsPage = ({ initialTab }) => {
    const [tab, setTab] = useState(VALID_TABS.includes(initialTab) ? initialTab : 'fleet');
    const [now, setNow] = useState(() => Date.now());

    /* Aktif sekmenin SEÇİLİ haftası ve o haftanın hakediş durumu. Üstteki akış bandı bunu
       gösterir; yoksa bant her zaman "Hafta sürüyor" yanıp altındaki kapanmış dönem listesiyle
       çelişiyordu. Hafta seçimi olmayan sekmeler (mesai, faturalar) null bırakır → bant
       yürüyen döneme döner. */
    const [weekCtx, setWeekCtx] = useState(null);

    useEffect(() => {
        if (VALID_TABS.includes(initialTab)) setTab(initialTab);
    }, [initialTab]);

    // Sekme değişince önceki sekmenin hafta bağlamı taşınmasın.
    useEffect(() => { setWeekCtx(null); }, [tab]);

    // Kalan süre dakikada bir tazelenir — saniye sayacı bu ekranda gürültü olurdu.
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 60000);
        return () => clearInterval(id);
    }, []);

    const handleTab = (id) => {
        setTab(id);
        const target = `payouts:${id}`;
        if (window.location.hash.replace('#', '') !== target) window.location.hash = target;
    };

    const period = useMemo(() => {
        const fleetClosing = nextClosing(7, now);
        const current = isoWeekOf(now);
        // Bant ve rozet SEÇİLİ dönemi anlatır; seçim yoksa yürüyen dönem.
        const shown = weekCtx ?? current;
        const isCurrent = compareWeeks(shown, current) === 0;
        const isFuture = compareWeeks(shown, current) > 0;
        const step = weekFlowStep(weekCtx, now);

        return {
            fleetClosing,
            current,
            shown,
            isCurrent,
            isFuture,
            complete: isFlowComplete(weekCtx),
            range: formatWeekRange(getWeekDateRange(shown.year, shown.week).start),
            remaining: humanizeUntil(fleetClosing.epoch, now),
            step,
            stepIndex: FLOW_ORDER.indexOf(step),
        };
    }, [now, weekCtx]);

    const active = TABS.find(t => t.id === tab) || TABS[0];

    /* Bandın altındaki tek satır: seçili dönem için SIRADAKİ gerçek olay. Sabit "şu kadar sonra
       kapanır" metni, geçmiş bir hafta seçiliyken yanlış bilgiydi. */
    const renderFlowNote = () => {
        if (period.isFuture) {
            return (
                <>
                    <strong>Bu dönem henüz başlamadı</strong>
                    <span>{period.shown.week}. hafta {period.range} tarihlerinde toplanacak.</span>
                </>
            );
        }

        if (period.isCurrent) {
            return (
                <>
                    <strong>{period.remaining} sonra</strong>
                    <span>
                        {formatClosing(period.fleetClosing.epoch)} — {period.fleetClosing.week}. hafta kapanır,
                        hakediş kayıtları o an oluşur.
                    </span>
                </>
            );
        }

        const notes = {
            close: ['Dönem kapandı', 'Bu haftaya ait hakediş kaydı açılmadı. Kayıt yalnızca teslimat veya mesai olan dönemler için üretilir.'],
            invoice: ['Fatura bekleniyor', 'Dönem kapandı, hakediş oluştu. Faturayı hakediş tutarıyla birebir aynı kesip yükleyin.'],
            approve: ['Hızlıyo onayında', 'Fatura yüklendi. Tutar ve belge kontrolü tamamlanınca ödemeye geçer.'],
            paid: period.complete
                ? ['Dönem tamamlandı', 'Hakediş ödendi, bu dönem için yapılacak işlem kalmadı.']
                : ['Ödeme bekleniyor', 'Hakediş onaylandı. Ödeme yapıldığında durum "Ödendi"ye geçer.'],
        };

        const [title, detail] = notes[period.step] ?? notes.close;
        return (<><strong>{title}</strong><span>{detail}</span></>);
    };

    return (
        <div className="payouts-page">
            <header className="payouts-head">
                <div className="payouts-head-text">
                    <h1>Hakedişler</h1>
                    <p>Filo kazancı, kurye hakedişleri ve faturalar tek yerde.</p>
                </div>
                <div className="payouts-period">
                    <span className="payouts-period-label">{period.isCurrent ? 'Yürüyen dönem' : 'Seçili dönem'}</span>
                    <strong>{period.shown.year}, {period.shown.week}. hafta</strong>
                    <span className="payouts-period-range">{period.range}</span>
                </div>
            </header>

            {/* İmza öğe: paranın yolu. Aktif adım SEÇİLİ dönemin gerçekten nerede olduğunu gösterir;
                geçilmiş adımlar da işaretlenir — akış "nerede kaldı" bakışta okunmalı. */}
            <section className="payout-flow" aria-label="Hakediş akışı">
                <div className="payout-flow-track">
                    {FLOW.map((step, i) => {
                        const isCurrent = step.key === period.step && !period.complete;
                        const isDone = period.complete || i < period.stepIndex;
                        return (
                            <div
                                key={step.key}
                                className={`payout-step ${isCurrent ? 'is-current' : ''} ${isDone ? 'is-done' : ''}`}
                                aria-current={isCurrent ? 'step' : undefined}
                            >
                                <span className="payout-step-dot" aria-hidden="true" />
                                <span className="payout-step-label">{step.label}</span>
                                <span className="payout-step-detail">{step.detail}</span>
                            </div>
                        );
                    })}
                </div>
                <div className="payout-flow-next">
                    <CalendarClock size={16} />
                    <div>{renderFlowNote()}</div>
                </div>
            </section>

            <nav className="payout-tabs" role="tablist" aria-label="Hakediş görünümleri">
                {TABS.map(t => {
                    const Icon = t.icon;
                    return (
                        <button
                            key={t.id}
                            role="tab"
                            aria-selected={tab === t.id}
                            className={`payout-tab ${tab === t.id ? 'active' : ''}`}
                            onClick={() => handleTab(t.id)}
                        >
                            <Icon size={16} />
                            <span>{t.label}</span>
                        </button>
                    );
                })}
            </nav>

            <p className="payout-tab-summary">{active.summary}</p>

            {tab === 'workday' && (
                <Notice tone="info" title="Hangi kurye bu listede görünür?">
                    <ul>
                        <li>
                            Ücret modeli <strong>saatlik</strong> veya <strong>yövmiye</strong> olan kuryeler.
                            Teslimat başı çalışan kuryenin mesai kaydı hiç oluşmaz — onun kazancı{' '}
                            <strong>Kurye hakedişi</strong> sekmesinde paket başına görünür.
                        </li>
                        <li>
                            Kaydın oluştuğu gün <strong>filonuza bağlı</strong> ve onaylı olan kuryeler. Kurye
                            filodan ayrıldıktan sonraki günler filonun listesine düşmez.
                        </li>
                        <li>O gün <strong>vardiya açmış</strong> olmak şart: vardiyasız günde satır üretilmez.</li>
                    </ul>
                </Notice>
            )}

            <div className="payout-panel" role="tabpanel">
                {tab === 'fleet' && <FleetPaymentsPage embedded onWeekContext={setWeekCtx} />}
                {tab === 'workday' && <WorkdayEarningsPage embedded />}
                {tab === 'courier' && <CourierPaymentsPage embedded onWeekContext={setWeekCtx} />}
                {tab === 'invoices' && <InvoicesPage embedded />}
                {tab === 'software-fee' && <SoftwareFeePage embedded />}
            </div>
        </div>
    );
};

export default PayoutsPage;
